import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  Calendar,
  Building2,
  CheckCircle,
  XCircle,
  FileCheck,
} from "lucide-react";
import { useEffect } from "react";

const StudentDetailsModal = ({ isOpen, onClose, student }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen || !student) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Fixed Modal Header */}
        <div className="sticky top-0 bg-[#4F22D8] text-white px-6 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
          <span className="text-xl text-white font-bold flex items-center gap-2">
            <User size={24} />
            Student Details
          </span>
          <button
            onClick={onClose}
            className="hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Modal Body */}
        <div className="overflow-y-auto flex-1 p-6">
          {/* Profile Section */}
          <div className="flex items-center gap-6 mb-6 pb-6 border-b border-gray-200">
            {student.photo && (
              <img
                src={student.photo}
                alt={student.fullName}
                className="w-24 h-24 rounded-full object-cover border-4 border-[#4F22D8]/20 flex-shrink-0"
              />
            )}
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {student.fullName}
              </h3>
              <div className="flex items-center gap-2 text-sm">
                <span
                  className={`px-3 py-1 rounded-full font-medium ${
                    student.status === "pending"
                      ? "bg-yellow-100 text-yellow-700"
                      : student.status === "approved"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {student.status.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-6">
            <h4 className="text-lg font-bold text-[#4F22D8] mb-4 flex items-center gap-2">
              <Mail size={20} />
              Contact Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-[#4F22D8] flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    <p className="text-sm font-medium text-gray-800 break-all">
                      {student.email}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      {student.isEmailVerified ? (
                        <>
                          <CheckCircle size={14} className="text-green-600" />
                          <span className="text-xs text-green-600">
                            Verified
                          </span>
                        </>
                      ) : (
                        <>
                          <XCircle size={14} className="text-red-600" />
                          <span className="text-xs text-red-600">
                            Not Verified
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <Phone size={18} className="text-[#4F22D8] flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Mobile</p>
                    <p className="text-sm font-medium text-gray-800">
                      {student.mobile}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      {student.isMobileVerified ? (
                        <>
                          <CheckCircle size={14} className="text-green-600" />
                          <span className="text-xs text-green-600">
                            Verified
                          </span>
                        </>
                      ) : (
                        <>
                          <XCircle size={14} className="text-red-600" />
                          <span className="text-xs text-red-600">
                            Not Verified
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                <div className="flex items-start gap-3">
                  <MapPin
                    size={18}
                    className="text-[#4F22D8] mt-1 flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 mb-1">Address</p>
                    <p className="text-sm font-medium text-gray-800">
                      {student.address}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <Calendar
                    size={18}
                    className="text-[#4F22D8] flex-shrink-0"
                  />
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Date of Birth</p>
                    <p className="text-sm font-medium text-gray-800">
                      {new Date(student.dateOfBirth).toLocaleDateString(
                        "en-IN"
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Educational Details */}
          <div className="mb-6">
            <h4 className="text-lg font-bold text-[#4F22D8] mb-4 flex items-center gap-2">
              <GraduationCap size={20} />
              Educational Details
            </h4>
            {student.institutionDetails?.map((institution, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#4F22D8]/5 to-[#4F22D8]/10 p-5 rounded-lg border border-[#4F22D8]/20 mb-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Building2
                      size={18}
                      className="text-[#4F22D8] mt-1 flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 mb-1">Institution</p>
                      <p className="text-sm font-semibold text-gray-800 break-words">
                        {institution.institutionName}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin
                      size={18}
                      className="text-[#4F22D8] mt-1 flex-shrink-0"
                    />
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Location</p>
                      <p className="text-sm font-medium text-gray-800">
                        {institution.location}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <GraduationCap
                      size={18}
                      className="text-[#4F22D8] mt-1 flex-shrink-0"
                    />
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Degree</p>
                      <p className="text-sm font-medium text-gray-800">
                        {institution.degree}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <FileCheck
                      size={18}
                      className="text-[#4F22D8] mt-1 flex-shrink-0"
                    />
                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        Field of Study
                      </p>
                      <p className="text-sm font-medium text-gray-800">
                        {institution.fieldOfStudy}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar
                      size={18}
                      className="text-[#4F22D8] mt-1 flex-shrink-0"
                    />
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Duration</p>
                      <p className="text-sm font-medium text-gray-800">
                        {institution.yearOfStart} -{" "}
                        {institution.targetedYearOfCompletion}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Information */}
          <div className="mb-6">
            <h4 className="text-lg font-bold text-[#4F22D8] mb-4">
              Additional Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">
                  Six Courses Completed
                </p>
                <div className="flex items-center gap-2">
                  {student.hasCompletedSixCourses ? (
                    <>
                      <CheckCircle size={18} className="text-green-600" />
                      <span className="text-sm font-medium text-green-600">
                        Yes
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle size={18} className="text-red-600" />
                      <span className="text-sm font-medium text-red-600">
                        No
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Applied Date</p>
                <p className="text-sm font-medium text-gray-800">
                  {new Date(student.createdAt).toLocaleDateString("en-IN", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Expiry Date</p>
                <p className="text-sm font-medium text-gray-800">
                  {new Date(student.expiredDate).toLocaleDateString("en-IN", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="mb-6">
            <h4 className="text-lg font-bold text-[#4F22D8] mb-4">Documents</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {student.photo && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Photo
                  </p>
                  <a
                    href={student.photo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#4F22D8] hover:underline text-sm flex items-center gap-2"
                  >
                    <FileCheck size={16} />
                    View Photo
                  </a>
                </div>
              )}

              {student.bonafideCertificate && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Bonafide Certificate
                  </p>
                  <a
                    href={student.bonafideCertificate}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#4F22D8] hover:underline text-sm flex items-center gap-2"
                  >
                    <FileCheck size={16} />
                    View Certificate
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl flex items-center justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentDetailsModal;
